import { S3_USER_MEDIA_BUCKET_LINK } from 'constants/imageUpload';
import { IMAGE_UPLOADED } from 'constants/mixpanel';
import { useEventListener } from 'hooks/useEventListener';
import { loadFonts } from 'hooks/useFont';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { EDITOR_REPLACE_MENU_TABLIST_ID } from 'modules/Editor/Editor.config';
import { copyActiveWidgets, pasteActiveWidgets, shouldIgnoreEventTarget } from 'modules/Editor/Editor.helpers';
import { getInitialWidgetPosition } from 'modules/Editor/Editor.helpers';
import { useBoundingBox } from 'modules/Editor/components/BoundingBox/useBoundingBox';
import { useContextMenu } from 'modules/Editor/components/ContextMenu/ContextMenuProvider';
import {
  RootState,
  addNewWidget,
  duplicatePage,
  setActiveWidget,
  store,
  useAppDispatch,
  useAppSelector,
} from 'modules/Editor/store';
import {
  selectActiveWidgetMenu,
  selectHasKeyboardIgnore,
  selectIsSlideView,
  selectIsUploading,
  selectReplaceWidgetMenuIndex,
} from 'modules/Editor/store/editorSettingsSelector';
import {
  setActiveWidgetMenu,
  setIsUploading,
  setReplaceWidgetMenuIndex,
} from 'modules/Editor/store/editorSettingsSlice';
import { ReplaceMenuTabIndex } from 'modules/Editor/store/editorSettingsSlice.types';
import { selectHasReachedPageLimit, selectPage, selectWidgets } from 'modules/Editor/store/infographSelector';
import { removeWidget } from 'modules/Editor/store/infographSlice';
import { addToPageClipboard } from 'modules/Editor/store/pageControlSlice';
import { selectActivePage, selectPageClipboard } from 'modules/Editor/store/pageSelector';
import { addToWidgetClipboard } from 'modules/Editor/store/widgetControlSlice';
import {
  selectActiveWidgetIds,
  selectActiveWidgets,
  selectHasActiveWidget,
  selectWidgetClipboard,
} from 'modules/Editor/store/widgetSelector';
import { GET_USER_MEDIA_API } from 'modules/User';
import { useImageUpload } from 'modules/User/useImageUpload';
import { memo, useEffect, useState } from 'react';
import { useSWRConfig } from 'swr';
import { WIDGET_MENU_OPTIONS } from 'types/WidgetMenu.types';
import { loadImageData } from 'utils/image';
import { clearLocalStorageItem, getLocalStorageItem } from 'utils/localStorage';
import { generateDefaultData as generateImageWidgetData } from 'widgets/ImageWidget/ImageWidget.helpers';
import { generateDefaultData as defaultTableData } from 'widgets/TextBasedWidgets/TableWidget/TableWidget.helpers';
import { formatHTMLTable } from 'widgets/TextBasedWidgets/TableWidget/formatHTMLTable';
import { generateDefaultData as defaultTextData } from 'widgets/TextBasedWidgets/TextWidget/TextWidget.helpers';

import { COPY_WIDGET_LOCAL_STORAGE_KEY } from './Clipboard.config';
import { clearBrowserClipboard, createCopyPage } from './Clipboard.helpers';

// handles global clipboard events in the editor for copying and pasting widgets / text / files / links...
export const Clipboard = memo(() => {
  const { boundingBox } = useBoundingBox();
  const [hasPastedWidget, setHasPastedWidget] = useState(false);
  const dispatch = useAppDispatch();
  const isUploading = useAppSelector(selectIsUploading);
  const { mutate } = useSWRConfig();

  const { hideMenu } = useContextMenu();
  const { hasValidUploadLimits, filterOutInvalidFiles, uploadImageFile, showUploadSuccessToast } = useImageUpload({
    isUploading,
  });

  const onAddPageToClipboard = () => {
    const storeState = store.getState() as RootState;
    const widgets = selectWidgets(storeState);
    const activePageId = selectActivePage(storeState);
    const activePage = selectPage(activePageId)(storeState);

    const copyPage = createCopyPage(activePage, widgets);
    dispatch(addToPageClipboard(copyPage));
  };

  /**
   * Add widget to canvas from localStorage if defined, or redux store
   */
  const onAddWidgetFromClipboard = () => {
    pasteActiveWidgets('Keyboard Paste');
    setHasPastedWidget(true);
  };

  const onAddPageFromClipboard = () => {
    const state = store.getState() as RootState;

    const hasReachedPageLimit = selectHasReachedPageLimit(state);
    if (hasReachedPageLimit) return;

    const pageClipboard = selectPageClipboard(state);
    const activePageId = selectActivePage(state);
    const pageCopies = pageClipboard;

    dispatch(duplicatePage({ insertAfterId: activePageId, pageClipboards: pageCopies }));
  };

  const clearWidgetClipboard = () => {
    dispatch(addToWidgetClipboard([]));
    clearLocalStorageItem(COPY_WIDGET_LOCAL_STORAGE_KEY);
  };

  const replaceClipboardWithPastedWidgets = () => {
    if (hasPastedWidget) {
      copyActiveWidgets();
      setHasPastedWidget(false);
    }
  };

  useEffect(replaceClipboardWithPastedWidgets, [hasPastedWidget]);

  const addPastedTextAsWidget = (pastedText: string) =>
    dispatch(addNewWidget(defaultTextData('paragraph', pastedText)));

  const addPastedTableAsWidget = (pastedHtml: string) => {
    if (!pastedHtml) return;

    const table = formatHTMLTable(pastedHtml);
    dispatch(addNewWidget(defaultTableData(table.content)));

    // update the bounding rect in case the fonts increase the size of the table
    loadFonts(table.fontList).finally(() => boundingBox.updateRect());
  };

  const addPastedFileAsImageWidget = async (pastedFiles: FileList) => {
    const files = Array.from(pastedFiles) as File[];
    const isValidUploadLimits = hasValidUploadLimits(files);
    if (!isValidUploadLimits) return;

    // if the replace menu is open - don't add the images as widgets to the page
    const state = store.getState() as RootState;
    const activeWidgetMenu = selectActiveWidgetMenu(state);
    const isReplaceMenuActive = activeWidgetMenu === WIDGET_MENU_OPTIONS.REPLACE;

    if (isReplaceMenuActive) {
      const activeWidgets = selectActiveWidgets(state);
      // does not support image upload
      if (activeWidgets[0].responsiveGroupId) return;

      // open the upload menu tab in the replace menu if its not already open
      const tabIndex = selectReplaceWidgetMenuIndex(state);
      if (tabIndex !== ReplaceMenuTabIndex.UPLOAD) {
        dispatch(setReplaceWidgetMenuIndex(ReplaceMenuTabIndex.UPLOAD));

        // need to focus the upload tab - else the internal focus handling can reset the tab index after the paste event
        const tabEl = document.getElementById(EDITOR_REPLACE_MENU_TABLIST_ID)?.children?.[ReplaceMenuTabIndex.UPLOAD];
        if (tabEl) (tabEl as HTMLElement).focus();
      }

      // else open the upload menu
    } else if (activeWidgetMenu !== WIDGET_MENU_OPTIONS.UPLOAD) {
      dispatch(setActiveWidgetMenu(WIDGET_MENU_OPTIONS.UPLOAD));
    }

    dispatch(setIsUploading(true));

    const filteredFiles = filterOutInvalidFiles(files);
    const uploadFilePromises = filteredFiles.map((file) => uploadImageFile(file));

    if (uploadFilePromises.length > 0) {
      await Promise.all(uploadFilePromises);

      // add the uploaded images as widgets
      const widgetList = [];

      // if multiple images are uploaded so they are not stacked on the same position
      let topAdjustment = 0;
      let leftAdjustment = 0;
      const offset = 10;

      for (const filePromise of uploadFilePromises) {
        const file = await filePromise;

        if (!file) continue;
        if (!file.file_id) continue;
        if (isReplaceMenuActive) continue;

        const { file_id: fileId } = file;

        // use the file id to generate the image url
        const url = `${S3_USER_MEDIA_BUCKET_LINK}${fileId}`;

        // get the image dimensions of the actual image
        // the image/height from the response are not reliable for the image dimensions
        let width = 0;
        let height = 0;

        try {
          const { naturalWidth, naturalHeight } = await loadImageData(url);
          width = naturalWidth;
          height = naturalHeight;
        } catch (e) {
          // should not get here but just in case
          console.error(e);
          continue;
        }

        const { top, left, infographWidth, infographHeight } = getInitialWidgetPosition();
        const widget = generateImageWidgetData(
          url,
          { width, height },
          { width: infographWidth, height: infographHeight },
          top + topAdjustment,
          left + leftAdjustment,
        );

        topAdjustment += offset;
        leftAdjustment += offset;

        widgetList.push(widget);
      }

      // add the uploaded images as one batch
      if (widgetList.length > 0) dispatch(addNewWidget(widgetList));

      // refetch the user media
      mutate(GET_USER_MEDIA_API);
      const uploadedImageCount = uploadFilePromises.length;
      showUploadSuccessToast(uploadedImageCount);
      Mixpanel.track(IMAGE_UPLOADED, { image_count: uploadedImageCount });
    }

    dispatch(setIsUploading(false));
  };

  const onCopy = (e: ClipboardEvent | Event) => {
    hideMenu();
    const event = e as ClipboardEvent;

    const target = event.target as HTMLElement;
    if (shouldIgnoreEventTarget(target)) return;

    const state = store.getState() as RootState;
    const hasKeyboardIgnore = selectHasKeyboardIgnore(state);
    if (hasKeyboardIgnore) return;

    const copiedText = event?.clipboardData?.getData('Text');
    if (copiedText) return;

    clearBrowserClipboard();

    const hasActiveWidget = selectHasActiveWidget(state);
    if (hasActiveWidget) return copyActiveWidgets('Keyboard');

    const isSlideView = selectIsSlideView(state);
    if (isSlideView) {
      clearWidgetClipboard();
      return onAddPageToClipboard();
    }
  };

  const removeActiveWidgets = (): void => {
    const storeState = store.getState() as RootState;
    const activePageId = selectActivePage(storeState);
    const activeWidgetIds = selectActiveWidgetIds(storeState);

    dispatch(setActiveWidget([]));
    dispatch(removeWidget({ pageId: activePageId, widgetIds: activeWidgetIds }));
  };

  const onCut = (e: ClipboardEvent | Event) => {
    hideMenu();
    const event = e as ClipboardEvent;

    const target = event.target as HTMLElement;
    if (shouldIgnoreEventTarget(target)) return;

    const state = store.getState() as RootState;
    const hasKeyboardIgnore = selectHasKeyboardIgnore(state);
    if (hasKeyboardIgnore) return;

    const copiedText = event?.clipboardData?.getData('Text');
    if (copiedText) return;

    clearBrowserClipboard();

    const hasActiveWidget = selectHasActiveWidget(state);
    if (hasActiveWidget) {
      copyActiveWidgets();
      return removeActiveWidgets();
    }
  };

  // TODO handle paste for: images / icons (svg) / spreadsheet tables / links
  const onPaste = (e: ClipboardEvent | Event) => {
    hideMenu();
    const event = e as ClipboardEvent;

    const target = event.target as HTMLElement;
    if (shouldIgnoreEventTarget(target)) return;

    const state = store.getState() as RootState;
    const hasKeyboardIgnore = selectHasKeyboardIgnore(state);
    if (hasKeyboardIgnore) return;

    const pastedText = event?.clipboardData?.getData('Text');
    const pastedHtml = event.clipboardData?.getData('text/html');
    const pastedFiles = event.clipboardData?.files;
    if (pastedHtml?.match('</table>')) return addPastedTableAsWidget(pastedHtml);
    if (pastedText) return addPastedTextAsWidget(pastedText);
    if (pastedFiles && pastedFiles.length > 0) return addPastedFileAsImageWidget(pastedFiles);

    clearBrowserClipboard();

    const widgetClipboard = selectWidgetClipboard(state);
    const hasWidgetClipboard = !!widgetClipboard.length || !!getLocalStorageItem(COPY_WIDGET_LOCAL_STORAGE_KEY);
    if (hasWidgetClipboard) return onAddWidgetFromClipboard();

    const pageClipboard = selectPageClipboard(state);
    const hasPageClipboard = !!pageClipboard.length;
    const isSlideView = selectIsSlideView(state);
    if (hasPageClipboard && isSlideView) return onAddPageFromClipboard();
  };

  useEventListener('paste', onPaste);
  useEventListener('copy', onCopy);
  useEventListener('cut', onCut);

  return <></>;
});
Clipboard.displayName = 'Clipboard';
